<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted, onBeforeUnmount } from 'vue'
import { useGoogleMapsLoader } from '~/composables/useGoogleMapsLoader'

type Pin = { lat: number; lng: number; title?: string; }

const DEFAULT_MARKER_URL_PATH = '/images/partials/map-pins/'
const MAX_NUMBERED_PINS = 14
const DEFAULT_MARKER_FALLBACK = 'default-pin.svg'
const config = useRuntimeConfig()

const props = defineProps<{
    open?: boolean
    modelValue?: boolean
    pins: Pin[]
    mapStyle?: google.maps.MapTypeStyle[]
    padding?: number
}>()

const emit = defineEmits<{
    (e: 'update:open', v: boolean): void
    (e: 'update:modelValue', v: boolean): void
}>()

const isOpen = computed<boolean>({
    get: () => (props.open ?? props.modelValue ?? false),
    set: v => {
        emit('update:open', v)
        emit('update:modelValue', v)
    }
})

const mapEl = ref<HTMLDivElement | null>(null)
let map: google.maps.Map | null = null
let markers: any[] = []
let hasCenteredOnUser = false

// --- user location state ---
const locating = ref(false)
let watchId: number | null = null
let userMarker: google.maps.Marker | null = null
let accuracyCircle: google.maps.Circle | null = null

const defaultStyle: google.maps.MapTypeStyle[] = [
    { elementType: 'geometry', stylers: [{ color: '#f7e4e4' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#7a2f2f' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f2b9b9' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#ffdede' }] },
]

function getMarkerSrc(index: number): string {
    const n = index + 1
    const file = n <= MAX_NUMBERED_PINS ? `pin-${n}.svg` : DEFAULT_MARKER_FALLBACK
    return DEFAULT_MARKER_URL_PATH + file
}

function createImgMarker(src: string, size = 54): HTMLElement {
    const wrap = document.createElement('div')
    wrap.style.width = `${size}px`
    wrap.style.height = `${size}px`
    wrap.style.display = 'inline-block'
    wrap.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,.25))'

    const img = document.createElement('img')
    img.src = src
    img.alt = ''
    img.style.width = '100%'
    img.style.height = '100%'
    img.style.objectFit = 'contain'

    wrap.appendChild(img)
    return wrap
}

// --- "blue dot" style marker for user position ---
function createUserLocationContent(): HTMLElement {
    const outer = document.createElement('div')
    outer.style.width = '18px'
    outer.style.height = '18px'
    outer.style.borderRadius = '9999px'
    outer.style.backgroundColor = 'rgba(66,133,244,0.3)'
    outer.style.border = '2px solid rgba(66,133,244,0.7)'
    outer.style.display = 'flex'
    outer.style.alignItems = 'center'
    outer.style.justifyContent = 'center'

    const inner = document.createElement('div')
    inner.style.width = '8px'
    inner.style.height = '8px'
    inner.style.borderRadius = '9999px'
    inner.style.backgroundColor = '#4285F4'

    outer.appendChild(inner)
    return outer
}

function clearMarkers() {
    markers.forEach(m => (m.map = null))
    markers = []
}

function destroyUserLocation() {
    if (watchId !== null) {
        try {
            navigator.geolocation.clearWatch(watchId)
        } catch {}
        watchId = null
    }

    if (userMarker) {
        try {
            userMarker.setMap(null)
        } catch {}
        userMarker = null
    }

    if (accuracyCircle) {
        try {
            accuracyCircle.setMap(null)
        } catch {}
        accuracyCircle = null
    }

    locating.value = false
    hasCenteredOnUser = false        // <-- reset
}

function destroyMap() {
    try { clearMarkers() } catch {}
    destroyUserLocation()
    try {
        // @ts-ignore
        google?.maps?.event?.clearInstanceListeners?.(map)
    } catch {}
    map = null
    if (mapEl.value) mapEl.value.innerHTML = ''
}

async function ensureMap() {
    if (!mapEl.value) return
    await useGoogleMapsLoader()
    if (!map) {
        const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary
        map = new Map(mapEl.value, {
            center: { lat: 46.0569, lng: 14.5058 },
            zoom: 8,
            disableDefaultUI: true,
            styles: props.mapStyle ?? defaultStyle,
            gestureHandling: 'greedy',
            mapId: config.public.googleMapsMapId || 'DEMO_MAP_ID',
        })
    }
}

async function renderPins() {
    if (!map) return
    clearMarkers()
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary
    props.pins.forEach((p, i) => {
        const src = getMarkerSrc(i)
        markers.push(new AdvancedMarkerElement({
            map,
            position: { lat: p.lat, lng: p.lng },
            title: p.title ?? `Location ${i + 1}`,
            content: createImgMarker(src, 54),
        }))
    })
}

function fitToPins() {
    if (!map) return
    const padding = props.padding ?? 56

    if (!props.pins.length) {
        map.setCenter({ lat: 46.0569, lng: 14.5058 })
        map.setZoom(7)
        return
    }

    if (props.pins.length === 1) {
        map.setCenter({ lat: props.pins[0].lat, lng: props.pins[0].lng })
        map.setZoom(14)
        return
    }

    const bounds = new google.maps.LatLngBounds()
    props.pins.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }))
    map.fitBounds(bounds, padding)
}

async function afterModalVisible() {
    await nextTick()
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
}

async function initOrUpdate() {
    await afterModalVisible()
    await ensureMap()
    await renderPins()
    try {
        // @ts-ignore
        google.maps.event.trigger(map, 'resize')
    } catch {}
    fitToPins()
}

// --- user location handling ---

async function updateUserLocation(position: GeolocationPosition) {
    if (!map) return

    const latLng = new google.maps.LatLng(
        position.coords.latitude,
        position.coords.longitude,
    )

    console.log('Got user position', position) // <- debug

    // create / update blue-dot marker
    if (!userMarker) {
        userMarker = new google.maps.Marker({
            map,
            position: latLng,
            title: 'Your location',
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
            },
            zIndex: 9999,
        })
    } else {
        userMarker.setPosition(latLng)
    }

    // accuracy circle
    const radius = position.coords.accuracy || 30
    if (!accuracyCircle) {
        accuracyCircle = new google.maps.Circle({
            map,
            center: latLng,
            radius,
            strokeColor: '#4285F4',
            strokeOpacity: 0.4,
            strokeWeight: 1,
            fillColor: '#4285F4',
            fillOpacity: 0.15,
            clickable: false,
            zIndex: 9998,
        })
    } else {
        accuracyCircle.setCenter(latLng)
        accuracyCircle.setRadius(radius)
    }

    if (!hasCenteredOnUser) {
        const currentZoom = map.getZoom() ?? 14
        if (currentZoom < 15) {
            map.setZoom(15)
        }
        map.setCenter(latLng)
        hasCenteredOnUser = true
    }
}

function startUserLocationWatch() {
    if (!map) return
    if (!('geolocation' in navigator)) {
        console.warn('Geolocation not supported in this browser')
        return
    }

    locating.value = true
    hasCenteredOnUser = false        // <-- new activation, allow one center

    watchId = navigator.geolocation.watchPosition(
        pos => {
            console.log('watchPosition success', pos)
            updateUserLocation(pos).catch(console.error)
        },
        err => {
            console.warn('Geolocation error:', err)
            if (err.code === err.PERMISSION_DENIED || err.code === err.POSITION_UNAVAILABLE) {
                destroyUserLocation()
            } else {
                locating.value = false
            }
        },
        {
            enableHighAccuracy: false,
            maximumAge: 60000,
            timeout: 30000,
        }
    )
}

function toggleUserLocation() {
    if (!map) return
    if (locating.value || watchId !== null) {
        destroyUserLocation()
    } else {
        startUserLocationWatch()
    }
}

// --- watchers / lifecycle ---

watch(isOpen, async (open, prev) => {
    if (open) {
        await initOrUpdate()
    } else if (prev) {
        destroyMap()
    }
})

watch(() => props.pins, () => {
    if (isOpen.value) initOrUpdate()
}, { deep: true })

onMounted(() => {
    if (isOpen.value) initOrUpdate()
})

onBeforeUnmount(() => {
    destroyMap()
})
</script>


<template>
    <UModal v-model:open="isOpen" class="map-modal" :unmount="false">
        <template #header>
            <div class="hidden"></div>
        </template>
        <template #body>
            <div class="p-0 w-full h-[60vh] relative">
                <ClientOnly>
                    <div ref="mapEl" class="w-full h-full overflow-hidden"></div>
                </ClientOnly>
                <UButton
                    icon="i-lyra-guides-x-black"
                    variant="outline"
                    class="absolute top-4 right-4 p-2 !h-auto ring-0 bg-white hover:bg-white active:bg-white shadow"
                    @click="isOpen = false"
                />
                <UButton
                    icon="lucide:locate-fixed"
                    variant="solid"
                    size="md"
                    class="absolute bottom-4 left-4 p-2 !h-auto bg-white text-secondary shadow"
                    @click="toggleUserLocation"
                />
            </div>
        </template>
    </UModal>
</template>


<style>
.map-modal {
    .modal-header { display: none; }
    .modal-body { padding: 0 !important; }
}
</style>
